<template>
  <div class="feeds-page">
    <div class="page-header mb-4">
      <div class="flex align-items-center justify-content-between">
        <div>
          <h1 class="text-900 mb-1">Выгрузки</h1>
          <p class="text-600 mb-0">Управление выгрузками данных в различных форматах</p>
        </div>
        <div class="flex gap-2 flex-wrap">
          <Button
            label="Создать выгрузку"
            icon="pi pi-plus"
            class="p-button-primary"
            @click="openCreateDialog"
          />
        </div>
      </div>
    </div>

    <!-- Индикатор загрузки -->
    <div v-if="loading" class="flex justify-content-center align-items-center p-6">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <!-- Таблица выгрузок -->
    <DataTable
      v-else
      :value="feeds"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      responsiveLayout="scroll"
      stripedRows
      class="p-datatable-sm"
      :loading="loading"
      emptyMessage="Выгрузки не найдены"
    >
      <!-- Название -->
      <Column field="name" header="Название" :sortable="true">
        <template #body="slotProps">
          <div>
            <div class="font-semibold">{{ slotProps.data.name }}</div>
            <div v-if="slotProps.data.description" class="text-sm text-600">
              {{ slotProps.data.description }}
            </div>
          </div>
        </template>
      </Column>

      <!-- Формат -->
      <Column field="format" header="Формат" :sortable="true" style="width: 100px">
        <template #body="slotProps">
          <Tag :severity="getFormatSeverity(slotProps.data.format)" :value="slotProps.data.format.toUpperCase()">
            <template #default>
              <i :class="getFormatIcon(slotProps.data.format)" class="mr-1"></i>
              {{ slotProps.data.format.toUpperCase() }}
            </template>
          </Tag>
        </template>
      </Column>

      <!-- Публичная ссылка -->
      <Column header="Публичная ссылка" style="min-width: 300px">
        <template #body="slotProps">
          <div class="flex align-items-center gap-2">
            <InputText
              :value="getFeedUrl(slotProps.data)"
              readonly
              class="flex-grow-1 text-sm"
              style="font-family: monospace"
            />
            <Button
              icon="pi pi-copy"
              class="p-button-secondary p-button-sm"
              v-tooltip.top="'Копировать ссылку'"
              @click="copyLink(slotProps.data)"
            />
            <Button
              icon="pi pi-refresh"
              class="p-button-warning p-button-sm"
              v-tooltip.top="'Сгенерировать новый токен'"
              @click="regenerateToken(slotProps.data)"
            />
          </div>
        </template>
      </Column>

      <!-- Записей -->
      <Column field="cached_count" header="Записей" :sortable="true" style="width: 100px">
        <template #body="slotProps">
          <span v-if="slotProps.data.cached_count !== null">
            {{ slotProps.data.cached_count.toLocaleString() }}
          </span>
          <span v-else class="text-400">—</span>
        </template>
      </Column>

      <!-- Просмотров -->
      <Column field="access_count" header="Просмотров" :sortable="true" style="width: 100px">
        <template #body="slotProps">
          {{ slotProps.data.access_count?.toLocaleString() || 0 }}
        </template>
      </Column>

      <!-- Обновление -->
      <Column field="refresh_interval" header="Обновление" :sortable="true" style="width: 150px">
        <template #body="slotProps">
          {{ formatRefreshInterval(slotProps.data.refresh_interval) }}
        </template>
      </Column>

      <!-- Статус -->
      <Column field="is_active" header="Статус" :sortable="true" style="width: 100px">
        <template #body="slotProps">
          <Tag 
            :severity="slotProps.data.is_active ? 'success' : 'secondary'" 
            :value="slotProps.data.is_active ? 'Активна' : 'Отключена'"
          />
        </template>
      </Column>

      <!-- Действия -->
      <Column header="Действия" style="width: 150px">
        <template #body="slotProps">
          <div class="flex gap-1">
            <Button
              icon="pi pi-pencil"
              class="p-button-info p-button-sm"
              v-tooltip.top="'Редактировать'"
              @click="openEditDialog(slotProps.data)"
            />
            <Button
              icon="pi pi-refresh"
              class="p-button-success p-button-sm"
              v-tooltip.top="'Обновить кэш'"
              :loading="refreshingCache[slotProps.data.id]"
              @click="refreshCache(slotProps.data)"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-danger p-button-sm"
              v-tooltip.top="'Удалить'"
              @click="confirmDelete(slotProps.data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Диалог создания/редактирования -->
    <Dialog
      v-model:visible="showDialog"
      :header="isEditing ? 'Редактирование выгрузки' : 'Создание выгрузки'"
      :style="{ width: '50rem' }"
      :modal="true"
      :closable="true"
      @hide="resetForm"
    >
      <div class="p-fluid">
        <!-- Название -->
        <div class="field mb-4">
          <label for="feed-name" class="block text-900 font-medium mb-2">Название *</label>
          <InputText
            id="feed-name"
            v-model="form.name"
            :class="{ 'p-invalid': errors.name }"
            placeholder="Например: Выгрузка для Яндекс.Маркет"
          />
          <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
        </div>

        <!-- Описание -->
        <div class="field mb-4">
          <label for="feed-description" class="block text-900 font-medium mb-2">Описание</label>
          <Textarea
            id="feed-description"
            v-model="form.description"
            :autoResize="true"
            rows="2"
            placeholder="Описание выгрузки (опционально)"
          />
        </div>

        <div class="grid">
          <!-- Формат -->
          <div class="col-12 md:col-6 field mb-4">
            <label for="feed-format" class="block text-900 font-medium mb-2">Формат</label>
            <Dropdown
              id="feed-format"
              v-model="form.format"
              :options="formatOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Выберите формат"
              class="w-full"
            />
          </div>

          <!-- Период обновления -->
          <div class="col-12 md:col-6 field mb-4">
            <label for="feed-refresh" class="block text-900 font-medium mb-2">Период обновления</label>
            <Dropdown
              id="feed-refresh"
              v-model="form.refresh_interval"
              :options="refreshIntervalOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Выберите интервал"
              class="w-full"
            />
          </div>
        </div>

        <!-- Фильтры -->
        <Divider align="left">
          <span class="text-600">Фильтры</span>
        </Divider>

        <div class="grid">
          <!-- Статус -->
          <div class="col-12 md:col-6 field mb-4">
            <label class="block text-900 font-medium mb-2">Статус товара</label>
            <Dropdown
              v-model="form.filters.status"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Все"
              :showClear="true"
              class="w-full"
            />
          </div>

          <!-- Пол -->
          <div class="col-12 md:col-6 field mb-4">
            <label class="block text-900 font-medium mb-2">Пол</label>
            <Dropdown
              v-model="form.filters.gender"
              :options="filterOptions.genders || []"
              placeholder="Все"
              :showClear="true"
              class="w-full"
            />
          </div>

          <!-- Тип очков -->
          <div class="col-12 md:col-6 field mb-4">
            <label class="block text-900 font-medium mb-2">Тип очков</label>
            <MultiSelect
              v-model="form.filters.type"
              :options="filterOptions.types || []"
              placeholder="Выберите типы"
              :showClear="true"
              class="w-full"
              display="chip"
            />
          </div>

          <!-- Бренды -->
          <div class="col-12 md:col-6 field mb-4">
            <label class="block text-900 font-medium mb-2">Бренды</label>
            <MultiSelect
              v-model="form.filters.brands"
              :options="filterOptions.brands || []"
              placeholder="Выберите бренды"
              :showClear="true"
              :filter="true"
              class="w-full"
              display="chip"
              :maxSelectedLabels="3"
            />
          </div>

          <!-- Производители -->
          <div class="col-12 md:col-6 field mb-4">
            <label class="block text-900 font-medium mb-2">Производители</label>
            <MultiSelect
              v-model="form.filters.producers"
              :options="filterOptions.producers || []"
              placeholder="Выберите производителей"
              :showClear="true"
              :filter="true"
              class="w-full"
              display="chip"
              :maxSelectedLabels="3"
            />
          </div>

          <!-- Материал -->
          <div class="col-12 md:col-6 field mb-4">
            <label class="block text-900 font-medium mb-2">Материал</label>
            <MultiSelect
              v-model="form.filters.material"
              :options="filterOptions.materials || []"
              placeholder="Выберите материалы"
              :showClear="true"
              class="w-full"
              display="chip"
            />
          </div>

          <!-- Страна -->
          <div class="col-12 field mb-4">
            <label class="block text-900 font-medium mb-2">Страна</label>
            <MultiSelect
              v-model="form.filters.country"
              :options="filterOptions.countries || []"
              placeholder="Выберите страны"
              :showClear="true"
              :filter="true"
              class="w-full"
              display="chip"
            />
          </div>
        </div>

        <!-- Поля для выгрузки -->
        <Divider align="left">
          <span class="text-600">Поля для выгрузки</span>
        </Divider>

        <div class="field mb-4">
          <label class="block text-900 font-medium mb-2">Выберите поля, которые будут включены в выгрузку</label>
          <MultiSelect
            v-model="form.fields"
            :options="availableFields"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите поля"
            :filter="true"
            class="w-full"
            display="chip"
            :maxSelectedLabels="5"
          />
        </div>

        <!-- Чекбокс активности -->
        <div class="field mb-4">
          <div class="flex align-items-center">
            <Checkbox
              v-model="form.is_active"
              inputId="feed-active"
              :binary="true"
            />
            <label for="feed-active" class="ml-2">Выгрузка активна</label>
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="showDialog = false"
        />
        <Button
          :label="isEditing ? 'Сохранить' : 'Создать'"
          :icon="isEditing ? 'pi pi-check' : 'pi pi-plus'"
          class="p-button-primary"
          :loading="saving"
          @click="saveFeed"
        />
      </template>
    </Dialog>

    <!-- Тосты и диалоги подтверждения -->
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useProductSchema } from '../composables/useProductSchema'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import MultiSelect from 'primevue/multiselect'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import Checkbox from 'primevue/checkbox'
import ProgressSpinner from 'primevue/progressspinner'
import Divider from 'primevue/divider'

import apiService from '../services/apiService'

const toast = useToast()
const confirm = useConfirm()

// Состояние
const feeds = ref([])
const loading = ref(false)
const saving = ref(false)
const showDialog = ref(false)
const isEditing = ref(false)
const editingId = ref(null)
const refreshingCache = reactive({})
const filterOptions = ref({})

// Ошибки валидации
const errors = reactive({
  name: null
})

// Доступные поля продукта — генерируются из единой схемы (productSchemaStore).
// Источник истины: backend/schemas/product_fields.py (поля с is_exportable=True).
// Любая новая нативная или кастомная колонка появляется здесь автоматически.
const { exportable: schemaExportable } = useProductSchema()
const availableFields = computed(() =>
  [...schemaExportable.value]
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((f) => ({ label: f.display_name, value: f.key })),
)

// Опции формата
const formatOptions = [
  { label: 'JSON', value: 'json' },
  { label: 'CSV', value: 'csv' },
  { label: 'XLSX (Excel)', value: 'xlsx' }
]

// Опции интервала обновления
const refreshIntervalOptions = [
  { label: 'Не обновлять автоматически', value: 0 },
  { label: 'Каждый час', value: 3600 },
  { label: 'Каждые 6 часов', value: 21600 },
  { label: 'Каждый день', value: 86400 },
  { label: 'Каждую неделю', value: 604800 }
]

// Опции статуса товара
const statusOptions = [
  { label: 'Все', value: null },
  { label: 'Активные', value: 'active' },
  { label: 'Архивные', value: 'archive' }
]

// Форма
const getEmptyForm = () => ({
  name: '',
  description: '',
  format: 'json',
  refresh_interval: 86400,
  filters: {
    status: null,
    type: [],
    brands: [],
    producers: [],
    gender: null,
    material: null,
    country: null
  },
  fields: ['id', 'part_number', 'part_name', 'brand', 'category', 'images'],
  is_active: true
})

const form = reactive(getEmptyForm())

// Загрузка списка выгрузок
const loadFeeds = async () => {
  loading.value = true
  try {
    const response = await apiService.getDataFeeds()
    feeds.value = response.data.feeds || []
  } catch (error) {
    console.error('Ошибка загрузки выгрузок:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить список выгрузок',
      life: 5000
    })
  } finally {
    loading.value = false
  }
}

// Загрузка опций для фильтров
const loadFilterOptions = async () => {
  try {
    const response = await apiService.getFeedFilterOptions()
    filterOptions.value = response.data
  } catch (error) {
    console.error('Ошибка загрузки опций фильтров:', error)
    // Не показываем ошибку, т.к. это не критично
  }
}

// Открыть диалог создания
const openCreateDialog = () => {
  isEditing.value = false
  editingId.value = null
  Object.assign(form, getEmptyForm())
  showDialog.value = true
}

// Открыть диалог редактирования
const openEditDialog = (feed) => {
  isEditing.value = true
  editingId.value = feed.id
  
  // Заполняем форму данными выгрузки
  form.name = feed.name
  form.description = feed.description || ''
  form.format = feed.format
  form.refresh_interval = feed.refresh_interval
  form.filters = { ...getEmptyForm().filters, ...feed.filters }
  form.fields = feed.fields || ['id', 'part_number', 'part_name']
  form.is_active = feed.is_active
  
  showDialog.value = true
}

// Сбросить форму
const resetForm = () => {
  Object.assign(form, getEmptyForm())
  errors.name = null
}

// Валидация формы
const validateForm = () => {
  let isValid = true
  errors.name = null

  if (!form.name.trim()) {
    errors.name = 'Название обязательно'
    isValid = false
  }

  return isValid
}

// Сохранить выгрузку
const saveFeed = async () => {
  if (!validateForm()) return

  saving.value = true
  try {
    const data = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      format: form.format,
      refresh_interval: form.refresh_interval,
      filters: form.filters,
      fields: form.fields,
      is_active: form.is_active
    }

    if (isEditing.value) {
      await apiService.updateDataFeed(editingId.value, data)
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Выгрузка обновлена',
        life: 3000
      })
    } else {
      await apiService.createDataFeed(data)
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Выгрузка создана',
        life: 3000
      })
    }

    showDialog.value = false
    await loadFeeds()
  } catch (error) {
    console.error('Ошибка сохранения выгрузки:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сохранить выгрузку',
      life: 5000
    })
  } finally {
    saving.value = false
  }
}

// Подтверждение удаления
const confirmDelete = (feed) => {
  confirm.require({
    message: `Вы уверены, что хотите удалить выгрузку "${feed.name}"?`,
    header: 'Подтверждение удаления',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    acceptClass: 'p-button-danger',
    accept: () => deleteFeed(feed)
  })
}

// Удалить выгрузку
const deleteFeed = async (feed) => {
  try {
    await apiService.deleteDataFeed(feed.id)
    toast.add({
      severity: 'success',
      summary: 'Успешно',
      detail: 'Выгрузка удалена',
      life: 3000
    })
    await loadFeeds()
  } catch (error) {
    console.error('Ошибка удаления выгрузки:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось удалить выгрузку',
      life: 5000
    })
  }
}

// Копировать ссылку в буфер
const copyLink = async (feed) => {
  const url = getFeedUrl(feed)
  try {
    await navigator.clipboard.writeText(url)
    toast.add({
      severity: 'success',
      summary: 'Скопировано',
      detail: 'Ссылка скопирована в буфер обмена',
      life: 2000
    })
  } catch (error) {
    console.error('Ошибка копирования:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось скопировать ссылку',
      life: 3000
    })
  }
}

// Обновить кэш выгрузки
const refreshCache = async (feed) => {
  refreshingCache[feed.id] = true
  try {
    await apiService.refreshFeedCache(feed.id)
    toast.add({
      severity: 'success',
      summary: 'Успешно',
      detail: 'Кэш выгрузки обновлён',
      life: 3000
    })
    await loadFeeds()
  } catch (error) {
    console.error('Ошибка обновления кэша:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось обновить кэш',
      life: 5000
    })
  } finally {
    refreshingCache[feed.id] = false
  }
}

// Перегенерировать токен
const regenerateToken = (feed) => {
  confirm.require({
    message: 'После генерации нового токена старая ссылка перестанет работать. Продолжить?',
    header: 'Генерация нового токена',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Да, сгенерировать',
    rejectLabel: 'Отмена',
    accept: async () => {
      try {
        await apiService.regenerateFeedToken(feed.id)
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Новый токен сгенерирован',
          life: 3000
        })
        await loadFeeds()
      } catch (error) {
        console.error('Ошибка генерации токена:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: error.response?.data?.detail || 'Не удалось сгенерировать токен',
          life: 5000
        })
      }
    }
  })
}

// Получить URL выгрузки
const getFeedUrl = (feed) => {
  const baseUrl = window.location.origin
  return `${baseUrl}/api/public/feeds/${feed.access_token}`
}

// Получить иконку формата
const getFormatIcon = (format) => {
  const icons = {
    json: 'pi pi-code',
    csv: 'pi pi-file',
    xlsx: 'pi pi-file-excel'
  }
  return icons[format] || 'pi pi-file'
}

// Получить цвет тега формата
const getFormatSeverity = (format) => {
  const severities = {
    json: 'info',
    csv: 'secondary',
    xlsx: 'success'
  }
  return severities[format] || 'secondary'
}

// Форматирование интервала обновления
const formatRefreshInterval = (seconds) => {
  if (!seconds || seconds === 0) return 'Вручную'
  if (seconds === 3600) return 'Каждый час'
  if (seconds === 21600) return 'Каждые 6 часов'
  if (seconds === 86400) return 'Ежедневно'
  if (seconds === 604800) return 'Еженедельно'
  
  // Для нестандартных значений
  const hours = Math.floor(seconds / 3600)
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    return `Каждые ${days} ${getDaysWord(days)}`
  }
  return `Каждые ${hours} ${getHoursWord(hours)}`
}

// Склонение слова "час"
const getHoursWord = (n) => {
  const lastTwo = n % 100
  const lastOne = n % 10
  if (lastTwo >= 11 && lastTwo <= 14) return 'часов'
  if (lastOne === 1) return 'час'
  if (lastOne >= 2 && lastOne <= 4) return 'часа'
  return 'часов'
}

// Склонение слова "день"
const getDaysWord = (n) => {
  const lastTwo = n % 100
  const lastOne = n % 10
  if (lastTwo >= 11 && lastTwo <= 14) return 'дней'
  if (lastOne === 1) return 'день'
  if (lastOne >= 2 && lastOne <= 4) return 'дня'
  return 'дней'
}

// Инициализация при монтировании
onMounted(async () => {
  await Promise.all([
    loadFeeds(),
    loadFilterOptions()
  ])
})
</script>

<style scoped>
.feeds-page {
  padding: 1.5rem;
}

.page-header h1 {
  font-size: 1.5rem;
  font-weight: 600;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem 1rem;
}

:deep(.p-tag) {
  font-size: 0.75rem;
}

:deep(.p-inputtext[readonly]) {
  background-color: var(--surface-100);
  cursor: default;
}
</style>
